import { DisplayGameCardsCarousel } from "@/v2-components/pages/games/DisplayGameCardsCarousel";
import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import Fade from "embla-carousel-fade";
import { cn } from "@/libs/utils";
import { useLatest } from "react-use";
import { EmblaCarouselType } from "embla-carousel";
import LoadingSpinnerWebView from "@/components/webview/icon/loadingSpinnerWebView";

interface UseAutoplayOptions {
  delay?: number;
  enable?: boolean;
}

type AutoplayEvent = "next" | "play" | "pause" | "stop" | "goto";

const useAutoplay = ({
  delay = 5000, // milliseconds
  enable = false,
}: UseAutoplayOptions = {}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const listenersRef = useRef<Map<AutoplayEvent, Set<(data?: any) => void>>>(
    new Map(),
  );
  const countRef = useRef(0);

  const emit = useCallback((event: AutoplayEvent, data?: any) => {
    const listeners = listenersRef.current.get(event);
    if (listeners) {
      listeners.forEach((listener) => listener(data));
    }
  }, []);

  const subscribe = useCallback(
    (event: AutoplayEvent, listener: (data?: any) => void) => {
      if (!listenersRef.current.has(event)) {
        listenersRef.current.set(event, new Set());
      }
      listenersRef.current.get(event)!.add(listener);

      return () => {
        listenersRef.current.get(event)?.delete(listener);
      };
    },
    [],
  );

  const clearCurrentInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    if (isPlaying || !enable) return;
    clearCurrentInterval();
    setIsPlaying(true);
    emit("play");

    intervalRef.current = setInterval(() => {
      countRef.current += 50;
      if (countRef.current >= delay) {
        countRef.current = 0;
        emit("next");
      }
    }, 50);
  }, [delay, enable, isPlaying, clearCurrentInterval, emit]);

  const pause = useCallback(() => {
    if (!isPlaying) return;
    clearCurrentInterval();
    setIsPlaying(false);
    emit("pause");
  }, [isPlaying, clearCurrentInterval, emit]);

  const stop = useCallback(() => {
    clearCurrentInterval();
    countRef.current = 0;
    setIsPlaying(false);
    emit("stop");
  }, [clearCurrentInterval, emit]);

  const restart = useCallback(() => {
    stop();
    play();
  }, [stop, play]);

  const goto = useCallback(
    (index: number) => {
      restart();
      emit("next", index);
    },
    [emit],
  );

  useEffect(() => {
    if (enable) {
      play();
    } else {
      stop();
    }
  }, [enable]);

  useEffect(() => {
    return () => {
      clearCurrentInterval();
      listenersRef.current.clear();
    };
  }, [clearCurrentInterval]);

  return {
    isPlaying,
    play,
    pause,
    stop,
    goto,
    restart,
    subscribe,
  };
};

export default function AutoplayCarouselWebView({
  totalItems = 0,
  children,
  isLoading = false,
}: React.PropsWithChildren<{
  totalItems?: number;
  isLoading?: boolean;
}>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isPlaying, play, pause, subscribe, goto } = useAutoplay({
    delay: 5000,
    enable: totalItems >= 2,
  });
  const apiRef = useRef<EmblaCarouselType | null>(null);
  const dotRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const currentIndexRef = useLatest(currentIndex);

  useEffect(() => {
    const _apiRef = apiRef.current;
    if (!_apiRef) return;

    const handleSelect = () => {
      if (_apiRef.selectedScrollSnap() !== currentIndexRef.current) {
        goto(_apiRef.selectedScrollSnap());
      }
    };

    // 드래그 해서 변경되는 경우
    _apiRef.on("select", handleSelect);

    return () => {
      _apiRef.off("select", handleSelect);
    };
  }, [goto]);

  useEffect(() => {
    const unsubscribeNext = subscribe("next", (_index) => {
      if (!apiRef.current) return;
      let index = apiRef.current.selectedScrollSnap() + 1;
      if (_index !== undefined) {
        index = _index;
      }
      setCurrentIndex(index % totalItems);
      apiRef.current.scrollTo(index);
    });

    const unsubscribePlay = subscribe("play", () => {
      if (!apiRef.current) return;
      const index = apiRef.current.selectedScrollSnap();
      const dotRef = dotRefs.current[index];
      if (dotRef) {
        dotRef.setAttribute("style", "animation-play-state: running");
      }
    });

    const unsubscribePause = subscribe("pause", () => {
      if (!apiRef.current) return;
      const index = apiRef.current.selectedScrollSnap();
      const dotRef = dotRefs.current[index];
      if (dotRef) {
        dotRef.setAttribute("style", "animation-play-state: paused");
      }
    });

    return () => {
      unsubscribePlay();
      unsubscribeNext();
      unsubscribePause();
    };
  }, [subscribe, totalItems]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <LoadingSpinnerWebView className="animate-spin text-white" />
      </div>
    );
  }

  if (!children || totalItems === 0) {
    return null;
  }

  return (
    <DisplayGameCardsCarousel
      ref={apiRef}
      onMouseEnter={(e: React.MouseEvent) => {
        pause();
      }}
      onMouseLeave={(e: React.MouseEvent) => {
        play();
      }}
      enableNavigations={false}
      options={{
        loop: totalItems >= 2,
      }}
      plugins={[Fade()]}
    >
      {children}
      {totalItems >= 2 && (
        <div className="absolute inset-x-0 bottom-5">
          <Dots
            ref={dotRefs}
            count={totalItems}
            currentIndex={currentIndex}
            onClick={(index) => {
              goto(index);
            }}
          />
        </div>
      )}
    </DisplayGameCardsCarousel>
  );
}

type DotsRef = Record<string, HTMLDivElement | null>;

interface DotsProps {
  count: number;
  currentIndex: number | null;
  onClick: (index: number) => void;
}

const Dots = forwardRef<DotsRef, DotsProps>(
  ({ count, currentIndex, onClick }, forwardedRef) => {
    const elementsRef = useRef<Record<string, HTMLDivElement | null>>({});

    useImperativeHandle(forwardedRef, () => elementsRef.current);

    return (
      <div className="flex items-center justify-center gap-x-2 laptop:gap-x-3">
        {Array.from({ length: count }, (_, index) => {
          const isActive =
            currentIndex === index || (currentIndex === null && index === 0);
          return (
            <div
              key={index}
              onClick={() => onClick(index)}
              className={cn(
                "relative h-1 cursor-pointer overflow-hidden rounded-full bg-white/50 laptop:h-2",
                isActive ? "w-4 laptop:w-6" : "w-1 laptop:w-2",
              )}
            >
              <div
                ref={(el) => {
                  elementsRef.current[index] = el;
                }}
                className={cn(
                  "absolute h-full w-full rounded-full bg-white",
                  currentIndex === index
                    ? "-left-full animate-progress"
                    : "hidden",
                )}
              />
            </div>
          );
        })}
      </div>
    );
  },
);

Dots.displayName = "MainBanners::Dots";
